import type { TelemetryEvent } from "@nova/schemas"

const optionalFields = ["elevation", "horizontalSpeed", "verticalSpeed", "windSpeed"] as const

const meanOf = (values: (number | undefined)[]) => {
    let sum = 0
    let count = 0
    for (const value of values) {
        if (value !== undefined && !Number.isNaN(value)) {
            sum += value
            count += 1
        }
    }
    return count === 0 ? undefined : sum / count
}

const meanFov = (values: (number | number[] | undefined)[]) =>
    values.some(Array.isArray)
        ? [0, 1].map(
              (axis) =>
                  meanOf(values.map((value) => (Array.isArray(value) ? value[axis] : undefined))) ??
                  NaN
          )
        : meanOf(values.filter((value): value is number => typeof value === "number"))

export const smooth = (
    events: TelemetryEvent[],
    samplesPerSecond: number,
    minSamplesPerSecond: number
) => {
    const window = Math.floor(samplesPerSecond / minSamplesPerSecond)
    if (window <= 1) return events
    const smoothed: TelemetryEvent[] = []
    for (let start = 0; start < events.length; start += window) {
        const bucket = events.slice(start, start + window)
        const averaged: TelemetryEvent = {
            timestamp: meanOf(bucket.map((event) => event.timestamp)) ?? NaN,
            longitude: meanOf(bucket.map((event) => event.longitude)) ?? NaN,
            latitude: meanOf(bucket.map((event) => event.latitude)) ?? NaN,
            yaw: meanOf(bucket.map((event) => event.yaw)) ?? NaN,
            pitch: meanOf(bucket.map((event) => event.pitch)) ?? NaN,
            roll: meanOf(bucket.map((event) => event.roll)) ?? NaN,
            height: meanOf(bucket.map((event) => event.height)) ?? NaN,
        }
        for (const field of optionalFields) {
            const mean = meanOf(bucket.map((event) => event[field]))
            if (mean !== undefined) averaged[field] = mean
        }
        const fov = meanFov(bucket.map((event) => event.fov))
        if (fov !== undefined) averaged.fov = fov
        smoothed.push(averaged)
    }
    return smoothed
}
